// @givmo/board — EVERY word CBG-YR6-MEET, -CONSENT and -APPROVE add, in one place: a committee's
// meetings, its written consents, and its minutes approved its own way (design-155's frames c1, c2,
// c4, c5 and c8; the backend's `contracts/board-committee-meetings.md`).
//
// ══ WHICH WORDS ARE APPROVED ════════════════════════════════════════════════════════════════════
//
// The lines marked APPROVED are reused word for word from shipped screens and say where from.
// Jonathan approved every other line as drafted, the Committees page's description among them, in
// the next part of copy round CBG-YR6-COPY1 (2026-10-16 11:54 PDT, "go with your recs";
// `scratchpad/2026-10-15-yr6meet-copy-round/copy-review.html`). A line added since is marked DRAFT
// where it sits, until its own round. Each names the frame that draws it, or "not drawn" for a state
// no frame shows. Where a committee's line is the board's approved line with the committee in the
// board's place (the derivation's §3.3), it says so.
//
// ⚠ Plan §3.2 and §3.4 bind every line: what the committee's records say and what Board does, never
// whether a committee may act for the board, never "must" or "should". A person is named in full; no
// pronoun for a named person. `committees-words.test.ts` holds every line here to the never-said list.

import { joinWords } from '../roster/officer-roles';
import type { NotAskedPerson } from '../minutes/minutes-signatures-words';

/** "1 day", "3 days" — as the board's notice line counts. */
const days = (count: number) => (count === 1 ? '1 day' : `${count} days`);

/** The record-not-decisions line every approval says. APPROVED (CBG-RC11-COPY1). */
const RECORD_NOT_DECISIONS =
  'Approving minutes approves the record of what happened, not the decisions in it again.';

/** While no rule of the committee's own is recorded: the board's way stands in (§3.3). */
const FROM_THE_BOARDS_WAY =
  'Givmo starts from your board’s way until the committee’s own is recorded on its page.';

export const committeeMeetingsWords = {
  // ── Scheduling a meeting (frame c4) ─────────────────────────────────────────────────────────────

  // Drawn, c4.
  whoIsMeeting: 'Who is meeting',
  // Drawn, c4.
  theBoard: 'The board',
  /** Drawn, c4: the committee's members, voting or not, as its page lists them. */
  invitesMembers: (committee: string, names: readonly string[]) =>
    `Invites the ${committee}’s members and sends them its packet: ${joinWords(names)}.`,
  // Not drawn: a committee with no member recorded.
  invitesNobody: (committee: string) =>
    `No members of the ${committee} are recorded, so no one is invited or sent its packet.`,
  // Drawn, c4.
  committeeNotice: (committee: string) =>
    `Notice is counted against the ${committee}’s own notice period.`,

  // Not drawn: a committee's meeting whose committee's name didn't come with it.
  aCommittee: 'A committee',
  // Not drawn (review-188's F1): the committees couldn't be read, so none can be
  // chosen; the dialog offers Try again, and a committee's meeting waits for it.
  committeesUnread:
    'We couldn’t read your committees, so this meeting can’t be scheduled for one just now.',
  /**
   * Not drawn (review-188's F1): the committee a kept draft chose has been
   * dissolved since — the backend's own refusal (`COMMITTEE_CANNOT_MEET`), and the choice to make.
   */
  chosenCommitteeDissolved: (committee: string | null) =>
    `${committee === null ? 'That committee' : `The ${committee}`} has been dissolved, so no meeting can be scheduled for it. Choose who is meeting.`,

  // ── A committee meeting's notice, on the Meetings list (frame c4's list) ────────────────────────
  // The board's approved facts, the committee's period for "your notice period".

  // Drawn, c4 ("Audit Committee · No notice period recorded for the committee").
  noNoticePeriod: 'No notice period recorded for the committee',
  // Not drawn: a period and no count yet.
  itsNoticePeriod: (period: number, cited: string) =>
    `Its notice period is ${days(period)}${cited}`,
  // Drawn, c4 ("Executive Committee · Scheduled …").
  scheduledAhead: (given: number, period: number, cited: string) =>
    `Scheduled ${days(given)} ahead · its notice period is ${days(period)}${cited}`,
  // Not drawn (review-188's F4): the director's notice block under a committee
  // meeting — the board's "{N} days fewer than the period you recorded." with the committee's period.
  noticeShortfall: (shortfall: number, committee: string) =>
    `${days(shortfall)} fewer than the ${committee}’s notice period.`,
  // Not drawn: a meeting entered after its date.
  enteredAfter: (after: number, period: number, cited: string) =>
    `Entered ${days(after)} after the meeting · its notice period is ${days(period)}${cited}`,

  // ── The Committees page (frame c1) ──────────────────────────────────────────────────────────────

  // Drawn, c1: the approved description, its meetings back in it.
  pageDescription:
    'Your board’s committees: who serves on each, when they meet, and their charters.',
  // Drawn, c1.
  columnNextMeeting: 'Next meeting',

  // ── One committee's page (frame c2) ─────────────────────────────────────────────────────────────

  // APPROVED (the Meetings page's button, `meetings-page.tsx`).
  scheduleMeeting: 'Schedule a meeting',
  // Drawn, c2.
  meetingsTitle: 'Meetings',
  // Not drawn: a committee that has met never and has nothing scheduled.
  noMeetings: 'No meetings scheduled.',
  /** Drawn, c2 ("Minutes drafted", "Minutes approved"); "No minutes yet" not drawn. A meeting still
   *  ahead says nothing. */
  minutesState: (state: string | null) =>
    state === 'approved' || state === 'imported'
      ? 'Minutes approved'
      : state === 'draft' || state === 'submitted'
        ? 'Minutes drafted'
        : 'No minutes yet',
  /** Drawn, c2 ("Written consent of its members" · "From your board’s way. No rule is recorded for
   *  the committee."): the way the board's rule gives, in the committee's words. */
  fromYourBoardsWay: 'From your board’s way. No rule is recorded for the committee.',
  /** Drawn, c2, on the authority card under a "Yes" answer: the committee's line-8 clock runs only
   *  then (the backend's §6.2, the derivation §4.4). */
  clockRuns: 'Its meetings and written actions show their line-8 dates.',

  // ── A seat's committees (frame c8; the member page, not drawn) ──────────────────────────────────

  // Drawn, c8. The names as the registry records them (the frame shortened them).
  yourCommittees: (parts: readonly string[]) => `Committees: ${parts.join(' · ')}`,
  // Drawn, c8.
  committeeWithRole: (name: string, chair: boolean) => (chair ? `${name} (chair)` : name),

  /** Not drawn: "Who's coming" on a committee's meeting, the board's approved lead with its members
   *  (who may not be directors) in the directors' place. */
  committeeReplies: (offersRecording: boolean) =>
    offersRecording
      ? 'The committee members’ replies. You can record a reply someone gave you in person.'
      : 'The committee members’ replies.',

  /** Not drawn: the vote dialog at a committee's meeting, the board's approved line (CBG-YR3-ROLES,
   *  "… isn't a director, so there's no vote to enter.") with the committee's vote. */
  voteNotOnCommittee: (names: string, count: number) =>
    `${names} ${count === 1 ? 'doesn’t' : 'don’t'} vote on the committee, so there’s no vote to enter.`,

  // ── A committee meeting's packet (not drawn): its members read it, not the whole board ─────────

  /** The packet note's field note, the board's approved words with the committee's members. */
  packetNoteReaders: (committee: string) =>
    `A note is packet contents — every member of the ${committee} reads it, and it stays in the version once you issue it.`,
  /** The packet's comments, the board's approved words with the committee's members. */
  packetCommentReaders: (committee: string) =>
    `These are the comments every member of the ${committee} can read. Members can also keep private notes on a packet — those are theirs alone and nobody else sees them, including you.`,

  // ── A committee's written consent (CBG-YR6-CONSENT; not drawn) ──────────────────────────────────

  // Not drawn: the draft dialog's choice, shown only once the board has a committee.
  whoseConsent: 'Whose written consent',
  // Not drawn: the choice's note for a committee.
  goesToItsMembers: (committee: string) =>
    `It goes to the members who vote on the ${committee} when you put it to them.`,
  // review-188's P2's interim note ("Givmo's forms are written for the board: …") went with CBG-FIX86:
  // a committee's consent now starts from the committee's own forms (`committee-only-words.ts`).
  /** The derivation's §3.5 ("A written consent of the Executive Committee"): on its page and list. */
  consentOf: (committee: string) => `A written consent of the ${committee}`,
  // Not drawn: the consent page's button, and the circulate dialog's.
  putToTheCommittee: 'Put it to the committee',
  // Not drawn: the consent page's actions card while it is out.
  whileTheCommitteeSigns: 'While the committee is signing',
  // Not drawn: the circulate dialog's title.
  putTitleToCommittee: (title: string, committee: string) => `Put “${title}” to the ${committee}`,
  /** Not drawn: the board's approved intro (`officer-roles-words.ts`, `circulateIntro`), its members
   *  in the directors' place. */
  circulateIntro:
    'It’s recorded as signed once every one of these members has signed. The list is taken now and then fixed: a member added afterwards can’t sign this consent, and one who leaves afterwards is still one of the signatures it needs.',
  /** The derivation's §3.5 ("to the 3 members who vote on the Executive Committee"). */
  circulateWho: (count: number, committee: string) =>
    `Who will sign, as the committee stands now — the ${count} ${count === 1 ? 'member who votes' : 'members who vote'} on the ${committee}`,
  // Not drawn: no voting member recorded.
  circulateNobody: (committee: string) =>
    `No one recorded on the ${committee} votes on it, so there is nobody for this consent to go to. Record its members on the committee’s page first.`,
  // Not drawn: members recorded as not voting, named once.
  circulateNotAsked: (names: string, count: number) =>
    `${names} ${count === 1 ? 'doesn’t vote on the committee and isn’t' : 'don’t vote on the committee and aren’t'} asked to sign.`,
  // Not drawn: the email the circulation sends (`consent-delivery-words.ts`'s line, its members).
  circulateEmails:
    'Each member who votes on the committee gets an email saying a written consent is waiting for their signature.',
  // Not drawn: the toast.
  nowBeforeCommittee: (committee: string) => `This consent is now before the ${committee}.`,
  // Not drawn: a failed circulation the backend didn't explain.
  couldntPutToCommittee: 'We couldn’t put this to the committee. Please try again.',
  // Not drawn: the draft's toast.
  draftSaved: (committee: string) => `Draft saved. Nothing has gone to the ${committee} yet.`,
  /** Not drawn: the revise dialog's warning, the board's approved words with the committee's
   *  voting members in the board's place. */
  reviseWarning: (committee: string, signedCount: number) =>
    signedCount === 0
      ? `Changing the wording starts a new version and asks the ${committee} again from the beginning. It goes back out to the members who vote on it as they stand today, which may not be who it went to the first time.`
      : `Changing the wording starts a new version, and ${signedCount} ${signedCount === 1 ? 'signature already given stays' : 'signatures already given stay'} with the old wording. Everyone has to sign again — nobody consents to words they haven’t read. It goes back out to the members who vote on the ${committee} as they stand today, which may not be who it went to the first time.`,
  // Not drawn.
  reviseUnchanged:
    'Nothing has changed yet. A new version has to differ from the one the committee already has.',
  // Not drawn.
  reviseEmails:
    'Each member who votes on the committee gets an email saying the revised consent is waiting for their signature.',
  // Not drawn: the revise toast.
  newVersionBefore: (committee: string) => `A new version is before the ${committee}.`,
  /** Not drawn: a filed committee consent's record (the backend's `filed_consent_statement`). */
  filedSigners: (committee: string, signed: unknown, required: unknown) =>
    typeof signed !== 'number' || typeof required !== 'number' || required === 0
      ? `the members of the ${committee} it went out to`
      : signed === required
        ? `every member of the ${committee} it went out to (${signed} of ${required})`
        : `${signed} of the ${required} members of the ${committee} it went out to`,

  // ── A committee's minutes, approved its own way (CBG-YR6-APPROVE, frame c5) ─────────────────────
  // RC11's approved lines, the committee in the board's place (the derivation's §3.3).

  // Drawn, c5.
  writtenConsentLine: (committee: string) =>
    `The ${committee} approves its minutes by written consent of its members: every member who votes on it signs.`,
  /** Drawn, c5 (no recusal); the recusal's forms, not drawn. "Givmo starts…" only while the
   *  committee's own rule isn't recorded. */
  writtenConsentWhy: (input: { recusedNames: readonly string[]; ownRuleRecorded: boolean }) => {
    const start = input.ownRuleRecorded ? [] : [FROM_THE_BOARDS_WAY];
    if (input.recusedNames.length === 0) {
      return [
        ...start,
        'Every member who votes on the committee signs one consent that names these minutes by their fingerprint. When the last one signs, they’re approved.',
        RECORD_NOT_DECISIONS,
      ];
    }
    const several = input.recusedNames.length > 1;
    return [
      `Every member who votes on the committee signs one consent that names these minutes by their fingerprint, ${joinWords(input.recusedNames)} included. When the last one signs, they’re approved.`,
      several
        ? 'Approving minutes approves the record of what happened, the recusals included. It doesn’t approve those decisions again: the members without a conflict decided them, and their records show that.'
        : 'Approving minutes approves the record of what happened, the recusal included. It doesn’t approve the decision again: the members without the conflict decided it, and its record shows that.',
      ...start,
    ];
  },
  // Not drawn.
  nextMeetingLine: (committee: string) =>
    `The ${committee} approves its minutes by a vote at its next meeting.`,
  // Not drawn.
  nextMeetingWhy: (ownRuleRecorded: boolean) => [
    ...(ownRuleRecorded ? [] : [FROM_THE_BOARDS_WAY]),
    'At the committee’s next meeting, record a motion to approve these minutes and its vote. When it carries, they’re approved, recorded against the person who presided.',
    RECORD_NOT_DECISIONS,
  ],
  // Not drawn (the backend's refusal leads with the same sentence).
  officerLine: (committee: string) =>
    `The ${committee}’s rules let an officer approve its minutes alone.`,
  // Not drawn.
  officerWhy: (ownRuleRecorded: boolean) => [
    ...(ownRuleRecorded ? [] : [FROM_THE_BOARDS_WAY]),
    'With this way, the minutes become the committee’s record on one officer’s approval, and the record says who approved them.',
  ],
  // Not drawn: a meeting way with no later meeting of the committee on the calendar.
  noNextMeeting: 'When the committee’s next meeting is on the calendar, record the motion there.',
  // Not drawn: a withdrawn consent, the meeting way beside a new one.
  withdrawnLine:
    'The written consent on these minutes was withdrawn. The committee can send a new one, or approve them by a vote at its next meeting.',
  // Not drawn.
  withdrawnWhy: (meeting: string | null) => [
    'A committee can approve its minutes by a vote at a meeting. Use this when a written consent on them can’t complete, for instance because a member won’t sign.',
    meeting !== null
      ? `At the committee’s ${meeting} meeting, record a motion to approve these minutes and its vote. When it carries, they’re approved, recorded against the person who presided.`
      : 'At the committee’s next meeting, record a motion to approve these minutes and its vote. When it carries, they’re approved, recorded against the person who presided.',
    RECORD_NOT_DECISIONS,
  ],
  // Not drawn: a closed session's own minutes, by the signatures of the members who were there.
  closedSignaturesLine: (committee: string) =>
    `A closed session of the ${committee} is approved by the signatures of its members who were there, your board’s way.`,
  // Not drawn.
  closedSignaturesWhy: [
    'Each member who votes on the committee and was in the session signs these minutes, and when the last one signs, they’re approved.',
    RECORD_NOT_DECISIONS,
  ],

  // Drawn, c5.
  remindTitle: 'Remind the committee',
  // Drawn, c5.
  remindLine: (committee: string) =>
    `Sends the ${committee}’s members a note that these minutes are waiting. Nothing is sent unless you send it.`,
  // Drawn, c5.
  signatureFollows:
    'A signature follows the committee’s approval, so there is nothing to sign yet.',

  // ── A committee's approved minutes, on their record (CBG-YR6-APPROVE; not drawn) ────────────────
  // The backend's approval statement for them says the same (`minutes_approval_statement`).

  /** RC9's approved record line (`minutes-consent-words.ts`, `recordApproval`), the committee's. */
  recordApproval: (committee: string, when: string) =>
    `Approved by a written consent of the ${committee}, signed by every member it went out to; the last signature on ${when}`,

  // ── A committee's minutes approved, on the minutes page and the record (review-188's F3) ────────
  // Not drawn: RC9's and RC10's lines with the committee's members where the board's
  // name its directors, as the backend says them (the seal note "…by written consent of the Finance
  // Committee's members", `approved_by_signatures_line`'s committee lines).

  /** RC9's minutes-page line (`minutes-consent-words.ts`, `approvedLine`), the committee's. */
  approvedByConsentLine: (committee: string, on: string) =>
    `Approved by written consent of the ${committee}’s members on ${on}.`,
  /** RC9's record event title (`eventTitle`), the committee's. */
  consentEventTitle: (committee: string) =>
    `Approved by written consent of the ${committee}’s members`,
  /** RC10's out line (`minutes-signatures-words.ts`, `outLine`), the committee's members. */
  signaturesOutLine: (signerCount: number, signedCount: number): string =>
    signerCount === 1
      ? `Out for the signature of the member who was there. ${signedCount} of 1 has signed.`
      : `Out for the signatures of the ${signerCount} members who were there. ${signedCount} of ${signerCount} ${signedCount === 1 ? 'has' : 'have'} signed.`,
  /** RC10's Signatures card before the ask (`signaturesBeforeAsk`), the committee's members. */
  signaturesBeforeAsk:
    'The members who were there sign here to approve these minutes once you ask for their signatures.',
  /** RC10's approved line (`approvedLine`), as the backend's committee line says it. */
  signaturesApprovedLine: (signedCount: number | null, lastOn: string): string =>
    signedCount === 1
      ? `Approved by the signature of the member who was in this session, on ${lastOn}.`
      : signedCount !== null && signedCount > 1
        ? `Approved by the signatures of the ${signedCount} members who were in this session; the last on ${lastOn}.`
        : `Approved by the signatures of the members who were in this session; the last on ${lastOn}.`,
  /** RC10's record line (`recordApproval`), its moment written out. */
  signaturesRecordApproval: (signedCount: number | null, when: string): string =>
    signedCount === 1
      ? `Approved by the signature of the member who was in this session, on ${when}`
      : signedCount !== null && signedCount > 1
        ? `Approved by the signatures of the ${signedCount} members who were in this session; the last on ${when}`
        : `Approved by the signatures of the members who were in this session; the last on ${when}`,
  /** RC10's record event title (`approvalEventTitle`), the committee's members. */
  signaturesEventTitle: 'Approved by the signatures of the members who were there',
  /** RC10's on-behalf entry (`onBehalfBody`), one of the committee's members who were there. */
  onBehalfBody:
    'This records that one of the members who were there signed to approve these minutes somewhere other than here — on paper, or in a message to you. It is recorded as your entry on their behalf, never as them signing in their own session.',
  /** RC10's on-behalf choice (`whichDirector`), the committee's members. */
  whichMember: 'Which member signed',
  /** RC10's select's empty choice (`chooseDirector`). */
  chooseMember: 'Choose a member',
  /**
   * RC10's not-asked line (`notAsked`), the committee's: its voting members who were there are asked
   * (the backend's §10.5), so anyone else the minutes record in the session doesn't vote on it.
   */
  notAsked: (people: readonly NotAskedPerson[], committee: string): string => {
    const each = people.map((person) =>
      person.reason === 'stepped_out'
        ? `${person.name}, who stepped out for a conflict of interest`
        : `${person.name}, who was in the session and doesn’t vote on the ${committee}`,
    );
    const listed =
      each.length <= 1 ? (each[0] ?? '') : `${each.slice(0, -1).join(', ')}, and ${each.at(-1)}`;
    return `Not asked: ${listed}.`;
  },
} as const;

/**
 * CBG-FIX39 (not drawn): minutes a written consent approved, once that consent's own record is no
 * longer kept — said from the record read's `approved_by_written_consent_kept`, never inferred from
 * the link's absence. The backend's approval statement says the same words.
 */
export const consentNoLongerKeptWords = {
  // FIX39's board line (tagged CBG-RC9), approved with the rest in CBG-YR6-COPY1's next part.
  board: (when: string) =>
    `Approved by a written consent of the directors on ${when}; that consent’s record is no longer kept.`,
  // The committee's: the board's line with the committee in its place.
  committee: (committee: string, when: string) =>
    `Approved by a written consent of the ${committee} on ${when}; that consent’s record is no longer kept.`,
} as const;
